/**
 * 地形タイルのエリア一覧。
 *
 * クライアントは一度に1枚のタイルだけを扱う（経路探索が単一グリッド前提のため）。
 * どのタイルを読むかを決めるには、読む前に範囲が分かっている必要があるので、
 * ここに静的な目録を持つ。タイル本体のヘッダにも同じ値が入っているため、
 * 読み込み後に照合して食い違いを検出する（terrainService）。
 *
 * ⚠️ `tools/terrain/build_osm_tile.py` の PRESETS と必ず一致させること。
 * 片方だけ変えると、範囲判定と実データがずれる。
 */
import type { LatLon } from "./types.js";

export interface TerrainArea {
  /** タイルのファイル名の接頭辞（`{id}_{adult|stroller}.uvkt`） */
  id: string;
  /** 画面に出す名前 */
  name: string;
  /** 現在地が取れないときの仮の出発地 */
  defaultPos: LatLon;
  /** その仮の出発地の呼び名（画面に「〇〇（現在地が未取得のため仮）」と出す） */
  defaultPosName: string;
  /** 検索欄のプレースホルダに出す例。エリアを変えたら例も変わるべき */
  searchExamples: string;
  /** 南西角 */
  originLat: number;
  originLon: number;
  cellsX: number;
  cellsY: number;
  cellM: number;
}

export const TERRAIN_AREAS: TerrainArea[] = [
  {
    id: "gotanda",
    name: "東京・武蔵小山〜不動前〜五反田〜目黒",
    defaultPos: { lat: 35.62534, lon: 139.71305 },
    defaultPosName: "不動前駅",
    searchExamples: "例：五反田駅、目黒駅、武蔵小山駅",
    originLat: 35.6178,
    originLon: 139.7012,
    cellsX: 320,
    cellsY: 288,
    // 武蔵小山駅まで含めると5mセルでは3.6MBで§12の2MB制限を超えるため7.5mセル
    // （面積は2.25倍になるがタイルは1.66MBで据え置き。解像度は5m→7.5mに粗くなる）
    cellM: 7.5,
  },
  {
    id: "kusatsu",
    name: "広島・草津地域全般",
    defaultPos: { lat: 34.37999, lon: 132.40242 },
    defaultPosName: "草津本町",
    searchExamples: "例：草津小学校、LECT、草津漁港",
    originLat: 34.3625,
    originLon: 132.388,
    cellsX: 288,
    cellsY: 336,
    // 広域のため7.5mセル。5mだと3.6MBで§12の2MB制限を超える
    // （Cloudflare Pagesはoctet-streamを圧縮しないので生サイズ＝転送量）
    cellM: 7.5,
  },
];

export const DEFAULT_AREA_ID = "gotanda";

export function areaById(id: string | undefined): TerrainArea {
  return TERRAIN_AREAS.find((a) => a.id === id) ?? TERRAIN_AREAS[0]!;
}

/** エリアの矩形（度） */
export function areaBounds(a: TerrainArea): {
  west: number;
  south: number;
  east: number;
  north: number;
} {
  const mPerDegLon = 111320 * Math.cos((a.originLat * Math.PI) / 180);
  return {
    west: a.originLon,
    south: a.originLat,
    east: a.originLon + (a.cellsX * a.cellM) / mPerDegLon,
    north: a.originLat + (a.cellsY * a.cellM) / 111320,
  };
}

export function isInArea(a: TerrainArea, pos: LatLon): boolean {
  const b = areaBounds(a);
  return pos.lat >= b.south && pos.lat <= b.north && pos.lon >= b.west && pos.lon <= b.east;
}

/** その座標を含むエリア。無ければ null（＝どのタイルでも計算できない場所） */
export function findAreaFor(pos: LatLon): TerrainArea | null {
  return TERRAIN_AREAS.find((a) => isInArea(a, pos)) ?? null;
}

/** エリアの大きさを「東西×南北 km」で表す（画面の案内文用） */
export function areaSizeLabel(a: TerrainArea): string {
  const w = (a.cellsX * a.cellM) / 1000;
  const h = (a.cellsY * a.cellM) / 1000;
  return `${w.toFixed(2)}×${h.toFixed(2)}km`;
}
